import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import useExchangeRate from '../hooks/useExchangeRate';

const API = 'https://affinityconcept.tech/api';

const SUPPORTED = ['BTC', 'ETH', 'BNB', 'SOL', 'ADA', 'DOT', 'MATIC', 'LINK', 'AVAX', 'UNI', 'ATOM', 'XRP'];

const fmtAED = (n) => `AED ${Math.round(n).toLocaleString('en-GB')}`;
const emptyForm = { coin_name: '', symbol: '', quantity: '', avg_buy_price: '', currency: 'USD', notes: '' };

export default function Crypto({ token, onLogout }) {
  const [user, setUser] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [refreshing, setRefreshing] = useState(false);
  const { toAED } = useExchangeRate();

  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/crypto`, { headers }).then(r => setHoldings(r.data));

  useEffect(() => {
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data));
    load();
  }, []);

  const openAdd = () => { setEditId(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (h) => {
    setEditId(h.id);
    setForm({
      coin_name: h.coin_name || '', symbol: h.symbol || '',
      quantity: h.quantity ?? '', avg_buy_price: h.avg_buy_price ?? '',
      currency: h.currency || 'USD', notes: h.notes || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      symbol: form.symbol.toUpperCase(),
      quantity: parseFloat(form.quantity),
      avg_buy_price: parseFloat(form.avg_buy_price),
    };
    if (editId) await axios.put(`${API}/crypto/${editId}`, payload, { headers });
    else await axios.post(`${API}/crypto`, payload, { headers });
    await load();
    setShowForm(false);
    setEditId(null);
    setForm(emptyForm);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holding?')) return;
    await axios.delete(`${API}/crypto/${id}`, { headers });
    load();
  };

  const refreshPrices = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/crypto/update-prices`, {}, { headers });
      await load();
    } catch (err) {
      alert('Failed to refresh prices. Try again shortly.');
    } finally {
      setRefreshing(false);
    }
  };

  // current_price is stored in USD; avg_buy_price is in the holding's own currency
  const valueAED = (h) => toAED((h.quantity || 0) * (h.current_price || 0), 'USD');
  const investedAED = (h) => toAED((h.quantity || 0) * (h.avg_buy_price || 0), h.currency);

  const totalValue = holdings.reduce((s, h) => s + valueAED(h), 0);
  const totalInvested = holdings.reduce((s, h) => s + investedAED(h), 0);
  const totalPL = totalValue - totalInvested;
  const totalPLPct = totalInvested > 0 ? (totalPL / totalInvested) * 100 : 0;

  const Card = ({ label, value, sub, accent }) => (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent || 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );

  const inputCls = "border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500";

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">
        <Link to="/investments" className="text-sm text-gray-500 hover:text-green-600">← Back to Overview</Link>

        <div className="flex justify-between items-center mt-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="text-3xl">🪙</div>
            <h1 className="text-2xl font-bold text-gray-900">Crypto</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={refreshPrices} disabled={refreshing}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-50">
              {refreshing ? 'Refreshing…' : 'Refresh Prices 🔄'}
            </button>
            <button onClick={openAdd}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium">+ Add</button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card label="Portfolio Value" value={fmtAED(totalValue)} />
          <Card label="Total Invested" value={fmtAED(totalInvested)} />
          <Card label="Total P&L" value={`${totalPL >= 0 ? '+' : ''}${fmtAED(totalPL)}`}
            sub={`${totalPLPct >= 0 ? '+' : ''}${totalPLPct.toFixed(1)}%`}
            accent={totalPL >= 0 ? 'text-green-500' : 'text-red-500'} />
          <Card label="Coins" value={holdings.length} />
        </div>

        {/* Add / Edit form */}
        {showForm && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h2 className="text-base font-semibold mb-4">{editId ? 'Edit Holding' : 'New Holding'}</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input required placeholder="Coin name (e.g. Bitcoin)" value={form.coin_name}
                onChange={e => setForm({ ...form, coin_name: e.target.value })} className={inputCls} />
              <input required list="crypto-symbols" placeholder="Symbol (e.g. BTC)" value={form.symbol}
                onChange={e => setForm({ ...form, symbol: e.target.value.toUpperCase() })} className={inputCls} />
              <datalist id="crypto-symbols">
                {SUPPORTED.map(s => <option key={s} value={s} />)}
              </datalist>
              <input required type="number" step="any" placeholder="Quantity" value={form.quantity}
                onChange={e => setForm({ ...form, quantity: e.target.value })} className={inputCls} />
              <input required type="number" step="any" placeholder="Average buy price (per coin)" value={form.avg_buy_price}
                onChange={e => setForm({ ...form, avg_buy_price: e.target.value })} className={inputCls} />
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className={inputCls}>
                <option>USD</option>
                <option>AED</option>
              </select>
              <input placeholder="Notes" value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} className={inputCls} />
              <div className="flex gap-3 md:col-span-2">
                <button type="submit" className="bg-green-600 text-white px-5 py-2 rounded-xl text-sm font-medium">Save</button>
                <button type="button" onClick={() => { setShowForm(false); setEditId(null); }}
                  className="border border-gray-200 px-5 py-2 rounded-xl text-sm">Cancel</button>
              </div>
            </form>
            <p className="text-xs text-gray-400 mt-2">Prices auto-fetch from CoinGecko for: {SUPPORTED.join(', ')}</p>
          </div>
        )}

        {/* Coins table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Coin</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Symbol</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Avg Buy</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Current</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Value (AED)</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">P&L</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {holdings.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-400 text-sm">No crypto holdings yet.</td></tr>
                )}
                {holdings.map(h => {
                  const value = valueAED(h);
                  const invested = investedAED(h);
                  const pl = value - invested;
                  const plPct = invested > 0 ? (pl / invested) * 100 : 0;
                  return (
                    <tr key={h.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="p-4 text-sm font-medium text-gray-900">{h.coin_name}</td>
                      <td className="p-4 text-sm text-gray-500">{h.symbol}</td>
                      <td className="p-4 text-sm text-gray-700 text-right">{h.quantity}</td>
                      <td className="p-4 text-sm text-gray-500 text-right hidden md:table-cell">{h.currency} {h.avg_buy_price?.toLocaleString('en-GB')}</td>
                      <td className="p-4 text-sm text-gray-500 text-right hidden md:table-cell">
                        {h.current_price ? `$${h.current_price.toLocaleString('en-GB')}` : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="p-4 text-sm font-semibold text-gray-900 text-right">{fmtAED(value)}</td>
                      <td className={`p-4 text-sm font-medium text-right ${pl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {pl >= 0 ? '+' : ''}{fmtAED(pl)}
                        <span className="block text-xs">{plPct >= 0 ? '+' : ''}{plPct.toFixed(1)}%</span>
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(h)} className="text-gray-400 hover:text-green-600 text-sm mr-2">✏️</button>
                        <button onClick={() => handleDelete(h.id)} className="text-gray-400 hover:text-red-600 text-sm">🗑️</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
